/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	SET_DEVICE_SIM_LIST,
	SET_HOME_MODAL_VISIBILITY,
	SET_UPI_DATA,
	SET_UPI_STATUS_LOADING,
	useUpiData,
} from 'Slices';
import { IDispatcherType, IUserData } from 'types';
import {
	fetchDeviceBindedStatus,
	fetchInitTpapSdkStatus,
	fetchUpiProfile,
	fetchUpiReactivationStatus,
	TriggerNativeApiWrapper,
} from 'utils/nativeUpiHandler';
import { isEmpty } from 'utils/Utils';
import { triggerMerchantInfoApi, useMerchantInfo } from 'Slices/MercantInfoSlice';
import { ONBOARDING_REDIRECTION_PAGE, UPISteps } from 'utils/CONSTANT';
import { API_URL } from 'Services/CONSTANT';
import { sendEvent } from 'utils/Sentry';
import { SyncBank } from 'Services';
import { checkSettlementAccountMatch } from 'utils/deviceBindingHelper';
import { checkPermissions, closeNativeSupport, getAppInfo } from 'utils/nativeSupport';
import Toasty from 'components/atoms/Toasty';

const useUpiStatus = (redirectionPage: string, initiateOnRender = true) => {
	const dispatch = useDispatch<IDispatcherType>();
	const upiData = useUpiData();
	const merchantInfoDataFromSlice = useMerchantInfo();
	const [upiStep, setUpiStep] = useState<UPISteps>('' as UPISteps);
	const [upiErr, setUpiErr] = useState('');
	const [initTpap, setInitTpap] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (initiateOnRender) reinitiateHook();
	}, [initiateOnRender]);

	useEffect(() => {
		const { upiProfileDetails, reactivationStatus, initDeviceBinding, profileId, isUserPresent } = upiData;
		console.log('------upiData-----', upiData);
		(async () => {
			if (isUserPresent === false) {
				setUpiStep(UPISteps.BANK_SCREEN);
				dispatch(SET_UPI_STATUS_LOADING(false));
				setLoading(false);
				return;
			}
			if (upiProfileDetails) {
				if (redirectionPage == ONBOARDING_REDIRECTION_PAGE.MANDATE) await SyncBank({ identifier: 'DATA' });
				if (isEmpty(upiProfileDetails?.user_banks)) {
					setUpiStep(UPISteps.BANK_SCREEN);
					dispatch(SET_UPI_STATUS_LOADING(false));
					setLoading(false);
					return;
				}
				if (redirectionPage == ONBOARDING_REDIRECTION_PAGE.MANDATE) {
					const isSettlementAccountPresent = await checkSettlementAccountMatch(redirectionPage);
					if (!isSettlementAccountPresent) {
						setUpiStep(UPISteps.BANK_SCREEN);
						dispatch(SET_UPI_STATUS_LOADING(false));
						setLoading(false);
						return;
					}
				}
				setUpiStep(upiProfileDetails?.reclaimStatus ? UPISteps.RECLAIM_STATUS : UPISteps.UPI_ONBOARDING_COMPLETE);
				dispatch(SET_UPI_STATUS_LOADING(false));
				setLoading(false);
				return;
			}
			if (reactivationStatus) {
				setUpiStep(UPISteps.REACTIVATE);
				dispatch(SET_UPI_STATUS_LOADING(false));
				setLoading(false);
				return;
			}
			if (initDeviceBinding) {
				setUpiStep(UPISteps.UPGRADE);
				dispatch(SET_UPI_STATUS_LOADING(false));
				setLoading(false);
				return;
			}
			if (profileId == '') {
				setUpiStep(UPISteps.BANK_SCREEN);
				dispatch(SET_UPI_STATUS_LOADING(false));
				setLoading(false);
				return;
			}
		})();
	}, [upiData]);

	const reinitiateHook = () => {
		dispatch(triggerMerchantInfoApi());
		if (isEmpty(upiData)) {
			fetchReactivationStatus();
		}
	};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initTpapSdk = async (userMobile: string) => {
		const permissions = checkPermissions();
		permissions.then(async (data: any) => {
			if (!data.READ_PHONE_STATE || !data.SEND_SMS) {
				dispatch(SET_HOME_MODAL_VISIBILITY({ hookPermissionModal: true }));
				setLoading(false);
			} else {
				try {
					const initSdkResp: any = await fetchInitTpapSdkStatus(false, userMobile);
					console.log(initSdkResp, '----init sdk-----');
					if (!initSdkResp?.bindingComplete) {
						dispatch(SET_UPI_DATA({ ...upiData, initDeviceBinding: true, upiDataFetched: true }));
						return;
					}
					dispatch(SET_UPI_DATA({ ...upiData, profileId: initSdkResp?.profileId }));
					if (initSdkResp?.profileId) {
						await fetchUpiProfileDetails(initSdkResp?.profileId);
					}
				} catch (err: any) {
					setUpiErr(err?.error || 'error in fetching upi details');
					sendEvent(err);
				}
			}
		});
	};

	useEffect(() => {
		if (initTpap && merchantInfoDataFromSlice?.data?.mobile) {
			const userMobile = merchantInfoDataFromSlice?.data?.mobile?.substring(2);
			initTpapSdk(userMobile);
			setInitTpap(false);
		}
	}, [initTpap, merchantInfoDataFromSlice?.data?.mobile]);

	const fetchUpiProfileDetails = async (profileId: string) => {
		try {
			const data = await fetchUpiProfile(profileId);
			const { profileDetails } = data as any;
			if (profileDetails) {
				dispatch(
					SET_UPI_DATA({ ...upiData, profileId: profileId, upiProfileDetails: profileDetails, upiDataFetched: true }),
				);
			}
		} catch (err: any) {
			try {
				if (err?.errorCode == '404') {
					await TriggerNativeApiWrapper(API_URL.create_user, 'POST', { profile_id: profileId }, getAppInfo() as any);
					fetchUpiProfileDetails(profileId);
					return;
				}
				setUpiErr(err?.error || 'error in fetching upi details');
				sendEvent(err);
			} catch (err: any) {
				setUpiErr(err?.error || 'error in fetching upi details');
				sendEvent(err);
			}
		}
	};

	const fetchDeviceBindingStatus = async () => {
		try {
			const deviceBindingData = await fetchDeviceBindedStatus();
			const { initiate_device_binding, simsList = [] } = deviceBindingData as any;
			dispatch(SET_DEVICE_SIM_LIST(simsList));
			let userStatus: IUserData;
			try {
				userStatus = await TriggerNativeApiWrapper(API_URL.user_upi_details, 'GET', {}, {});
				dispatch(SET_UPI_DATA({ userActivationStatus: userStatus.user_status }));
				if (redirectionPage == ONBOARDING_REDIRECTION_PAGE.PROFILE && userStatus.user_status === 'INACTIVE') {
					Toasty.error('USER INACTIVE');
					closeNativeSupport();
					return;
				}
				if (initiate_device_binding || userStatus.user_status === 'INACTIVE') {
					dispatch(
						SET_UPI_DATA({
							userActivationStatus: userStatus.user_status,
							initDeviceBinding: true,
							upiDataFetched: true,
						}),
					);
					return;
				}
				setInitTpap(true);
			} catch (err: any) {
				if (initiate_device_binding) {
					dispatch(SET_UPI_DATA({ initDeviceBinding: initiate_device_binding, upiDataFetched: true }));
					return;
				}
				setInitTpap(true);
			}
		} catch (err: any) {
			setUpiErr(err?.error || 'error in fetching upi details');
			sendEvent(err);
		}
	};

	const fetchReactivationStatus = async () => {
		try {
			const reactivationData: any = await fetchUpiReactivationStatus();
			fetchDeviceBindingStatus();
		} catch (err: any) {
			// err handling
			setUpiErr(err?.error || 'error in fetching upi details');
			sendEvent(err);
		}
	};
	return { upiStep, upiErr, loading, setLoading, initTpapSdk };
};

export default useUpiStatus;
